// ─────────────────────────────────────────────────────────────────────────────
// HOW FAR THROUGH THE TRAINING DECK A PARTNER IS.
//
// 🚨 "Start training" IS A LIE TO SOMEBODY FOUR MODULES IN, and it is the reason
// they would not click it: it reads as though their progress is gone.
//
// 🔑 ONE DERIVATION, BECAUSE TWO SURFACES ASK. The dashboard CTA needs this and
// the deck itself needs the storage key, and what is being counted is a
// localStorage array of module ids under a key spelled from a partner id. Two
// copies of that spelling is one typo away from a dashboard that greets a
// half-trained partner with "Start training" — the same class of split as a task
// card reading "done" while the payout gate reads "missing".
//
// ⚠️ IT LIVES IN utils/ AND NOT IN THE PAGE so it can be exercised rather than
// read. The two rules below — stale ids do not count, and an empty deck is not
// complete — are both silent when wrong, and a structural assertion on a .jsx
// page could not tell either of them from a comment.
//
// ⚠️ PER-DEVICE, AND THAT IS CONSISTENT RATHER THAN CORRECT. Progress is in
// localStorage, so a partner who read three modules on their phone sees "Start
// training" on their laptop — but the deck they open there shows no checkmarks
// either, so the button and the deck agree with each other. Moving progress to
// the server is its own piece of work; a label that disagreed with the deck
// beside it would be worse than one that is merely local.
// ─────────────────────────────────────────────────────────────────────────────

/** The one spelling of the key. The deck asks for it rather than building it. */
export const trainingProgressKey = (partnerId) => (partnerId
  ? `doorlypro_training_progress_${partnerId}`
  : null);

/**
 * @param {{partnerId:number|string|null, modules:Array<{id:string}>}} args
 * @returns {{done:number, total:number, started:boolean, complete:boolean}}
 */
export function trainingProgress({ partnerId, modules } = {}) {
  const list = Array.isArray(modules) ? modules : [];
  const total = list.length;

  const key = trainingProgressKey(partnerId);
  let stored = [];
  if (key) {
    // ⚠️ localStorage THROWS in some contexts rather than returning null — a
    // private window, site data blocked, a thumbnail capture. An exception here
    // would take the whole dashboard down over a label.
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
      const parsed = JSON.parse(raw || '[]');
      stored = Array.isArray(parsed) ? parsed : [];
    } catch (_) { stored = []; }
  }

  // ── 🚨 STALE IDS ARE NOT COUNTED ──────────────────────────────────────────
  //
  // The stored array is whatever the decks contained when this partner read
  // them. A module since renamed or removed would otherwise inflate the count —
  // reporting six of five, or worse, "complete" on a deck they have not
  // finished. Only ids in the CURRENT deck count, which also makes adding a
  // module correctly reopen the task for everybody.
  const current = new Set(list.map((m) => m && m.id).filter(Boolean));
  const done = stored.filter((id) => current.has(id)).length;

  return {
    done,
    total,
    started: done > 0,
    // 🔑 total > 0 GUARDS THE EMPTY DECK. Zero of zero is not "complete", it is
    // "we do not know yet which deck this person gets" — the dashboard computes
    // this before the partner has loaded, and reporting complete there would
    // hide the task on a page that had not finished rendering.
    complete: total > 0 && done >= total,
  };
}
